/**
 * @file PwaButton — drop-in toolbar widget for the PWA layer.
 *
 * Always renders, even when `initPwa()` was never called or the platform
 * does not support service workers, so it is safe to place unconditionally
 * in a header. The dropdown shows the running version (and the
 * available one alongside it when an update is pending); the trigger
 * icon and action area below adapt to the most relevant state
 * (update → install → reload → iOS-help → offline → online).
 */

const { computed } = Vue;
import { usePwa } from '../composables/pwa.js';

function formatVersionLine(version, deployedAt) {
  const v = version || 'unknown';
  if (!deployedAt) return v;
  const d = new Date(deployedAt);
  if (isNaN(d.getTime())) return v;
  return `${v} · ${d.toLocaleDateString()}`;
}

function isIosDevice() {
  if (typeof navigator === 'undefined') return false;
  if (/iPad|iPhone|iPod/.test(navigator.userAgent)) return true;
  return (
    navigator.platform === 'MacIntel' &&
    typeof navigator.maxTouchPoints === 'number' &&
    navigator.maxTouchPoints > 1
  );
}

export default {
  name: 'PwaButton',

  template: `
    <div class="dropdown">
      <button
        type="button"
        class="btn btn-secondary"
        data-bs-toggle="dropdown"
        aria-expanded="false"
      >
        <i :class="['bi', triggerIcon]"></i>
      </button>

      <ul class="dropdown-menu dropdown-menu-end p-0" style="min-width: 320px;" @click.stop>
        <li class="px-3 py-2">
          <div class="small text-muted">Running</div>
          <div class="small fw-semibold">{{ formattedCurrentVersion }}</div>
        </li>
        <li v-if="pwa.updateAvailable"><hr class="dropdown-divider my-0"></li>
        <li v-if="pwa.updateAvailable" class="px-3 py-2">
          <div class="small text-muted">Available</div>
          <div class="small fw-semibold">{{ formattedAvailableVersion }}</div>
        </li>
        <li v-if="hasAction"><hr class="dropdown-divider my-0"></li>
        <li v-if="hasAction" class="px-3 py-2">
          <div class="small mb-2">{{ actionMessage }}</div>
          <button
            v-if="primaryAction === 'update'"
            type="button"
            class="btn btn-primary btn-sm w-100"
            :disabled="pwa.installingUpdate"
            @click="onUpdate"
          >
            <template v-if="pwa.installingUpdate">
              <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>Preparing update…
            </template>
            <template v-else>
              <i class="bi bi-arrow-clockwise me-1"></i>Update now
            </template>
          </button>
          <button
            v-else-if="primaryAction === 'install'"
            type="button"
            class="btn btn-primary btn-sm w-100"
            @click="onInstall"
          >
            <i class="bi bi-download me-1"></i>Install
          </button>
          <button
            v-else-if="primaryAction === 'reload'"
            type="button"
            class="btn btn-primary btn-sm w-100"
            @click="onReload"
          >
            <i class="bi bi-arrow-repeat me-1"></i>Reload
          </button>
          <ol v-else-if="primaryAction === 'ios-help'" class="small mb-0 ps-3">
            <li>Tap the <i class="bi bi-box-arrow-up"></i> Share button.</li>
            <li>Choose <strong>Add to Home Screen</strong>.</li>
          </ol>
        </li>
        <li v-if="showInstallHint"><hr class="dropdown-divider my-0"></li>
        <li v-if="showInstallHint" class="px-3 py-2">
          <div class="small text-muted">
            <i class="bi bi-download me-1"></i>Install for offline use with Chrome or Edge
          </div>
        </li>
      </ul>
    </div>
  `,

  setup() {
    let pwa;
    try {
      pwa = usePwa();
    } catch (_) {
      // initPwa() was never called — render in version-only "no SW" state.
      pwa = {
        currentVersion: null,
        currentCommit: null,
        currentDeployedAt: null,
        availableVersion: null,
        availableCommit: null,
        availableDeployedAt: null,
        swEnabled: false,
        swActive: false,
        isInstalled: false,
        isInstallable: false,
        updateAvailable: false,
        installingUpdate: false,
        needsReloadForInstall: false,
        isOffline: false,
        install: async () => 'unavailable',
        applyUpdate: async () => {},
        refreshVersion: async () => {},
      };
    }

    const ios = isIosDevice();

    const primaryAction = computed(() => {
      if (pwa.updateAvailable) return 'update';
      if (!pwa.swEnabled) return 'none';
      if (pwa.isInstallable) return 'install';
      if (pwa.needsReloadForInstall && !ios && !pwa.isInstalled)
        return 'reload';
      if (ios && !pwa.isInstalled) return 'ios-help';
      if (pwa.swActive && pwa.isOffline) return 'offline';
      // 'online' only when not installed — installed users don't need the notice
      if (pwa.swActive && !pwa.isInstalled) return 'online';
      return 'none';
    });

    const showInstallHint = computed(
      () =>
        pwa.swEnabled &&
        (primaryAction.value === 'none' || primaryAction.value === 'online') &&
        !ios &&
        !pwa.isInstalled &&
        !pwa.isInstallable &&
        !pwa.needsReloadForInstall
    );

    const triggerIcon = computed(() => {
      switch (primaryAction.value) {
        case 'update':
          return 'bi-arrow-clockwise';
        case 'install':
        case 'ios-help':
          return 'bi-download';
        case 'reload':
          return 'bi-arrow-repeat';
        case 'offline':
          return 'bi-wifi-off';
        case 'online':
          return 'bi-cloud-check';
        default:
          return 'bi-info-circle';
      }
    });

    const actionMessage = computed(() => {
      switch (primaryAction.value) {
        case 'update':
          return 'A new version is available.';
        case 'install':
          return 'Install this app to use it offline.';
        case 'reload':
          return 'Reload the page to enable installing this app.';
        case 'ios-help':
          return 'Install this app on your home screen:';
        case 'offline':
          return 'You are offline. The app is running from its cached version.';
        default:
          return '';
      }
    });

    // 'online' excluded — replaced by showInstallHint when applicable
    const hasAction = computed(
      () => primaryAction.value !== 'none' && primaryAction.value !== 'online'
    );

    const formattedCurrentVersion = computed(() =>
      formatVersionLine(pwa.currentVersion, pwa.currentDeployedAt)
    );
    const formattedAvailableVersion = computed(() =>
      formatVersionLine(pwa.availableVersion, pwa.availableDeployedAt)
    );

    const onInstall = async () => {
      await pwa.install();
    };
    const onUpdate = async () => {
      await pwa.applyUpdate();
    };
    const onReload = () => {
      if (typeof window !== 'undefined') window.location.reload();
    };

    return {
      pwa,
      primaryAction,
      triggerIcon,
      actionMessage,
      hasAction,
      showInstallHint,
      formattedCurrentVersion,
      formattedAvailableVersion,
      onInstall,
      onUpdate,
      onReload,
    };
  },
};
